import { create } from 'zustand';
import type { AlertKind } from '../config/alerts';
import type { LatLng } from '../lib/geo';
import type { Gauge } from '../types/gauges';
import type { SearchPin } from '../types/search';

export type ToggleableAlertKind = Exclude<AlertKind, 'other'>;

export type PanelView =
  | { kind: 'list' }
  | { kind: 'alert'; alertId: string }
  | { kind: 'gauge'; gauge: Gauge }
  | { kind: 'station'; stationId: string }
  | { kind: 'pin'; pinId: string };

/** Bottom sheet detents on narrow screens (InterfaceExplorerView.swift). */
export type SheetDetent = 'small' | 'medium' | 'large';

export interface Toast {
  id: number;
  message: string;
}

interface AppState {
  /** Layer picker: five alert toggles plus Hill Country. All alert kinds default on. */
  alertToggles: Record<ToggleableAlertKind, boolean>;
  hillCountryVisible: boolean;
  /** Gauge visibility control: false shows only flooding gauges (iOS default). */
  showAllGauges: boolean;
  layerPickerOpen: boolean;
  panelView: PanelView;
  sheetDetent: SheetDetent;
  searchPins: SearchPin[];
  userLocation: LatLng | null;
  toast: Toast | null;

  setAlertToggle: (kind: ToggleableAlertKind, on: boolean) => void;
  setHillCountryVisible: (on: boolean) => void;
  setShowAllGauges: (on: boolean) => void;
  setLayerPickerOpen: (open: boolean) => void;
  setPanelView: (view: PanelView) => void;
  setSheetDetent: (detent: SheetDetent) => void;
  addSearchPin: (pin: SearchPin) => void;
  removeSearchPin: (id: string) => void;
  setUserLocation: (loc: LatLng | null) => void;
  showToast: (message: string) => void;
  clearToast: (id: number) => void;
}

let toastCounter = 0;

export const useAppStore = create<AppState>((set) => ({
  alertToggles: {
    flashFloodWarning: true,
    floodWarning: true,
    flashFloodWatch: true,
    floodWatch: true,
    floodAdvisory: true,
  },
  hillCountryVisible: false,
  showAllGauges: false,
  layerPickerOpen: false,
  panelView: { kind: 'list' },
  sheetDetent: 'small',
  searchPins: [],
  userLocation: null,
  toast: null,

  setAlertToggle: (kind, on) => set((s) => ({ alertToggles: { ...s.alertToggles, [kind]: on } })),
  setHillCountryVisible: (on) => set({ hillCountryVisible: on }),
  setShowAllGauges: (on) => set({ showAllGauges: on }),
  setLayerPickerOpen: (open) => set({ layerPickerOpen: open }),
  setPanelView: (view) => set({ panelView: view }),
  setSheetDetent: (detent) => set({ sheetDetent: detent }),
  addSearchPin: (pin) => set((s) => ({ searchPins: [...s.searchPins, pin] })),
  removeSearchPin: (id) => set((s) => ({ searchPins: s.searchPins.filter((p) => p.id !== id) })),
  setUserLocation: (loc) => set({ userLocation: loc }),
  showToast: (message) => set({ toast: { id: ++toastCounter, message } }),
  clearToast: (id) => set((s) => (s.toast?.id === id ? { toast: null } : {})),
}));

/** Opens a detail view; on narrow screens the sheet rises to medium like the iOS detail sheets. */
export function openDetail(view: Exclude<PanelView, { kind: 'list' }>): void {
  const s = useAppStore.getState();
  s.setPanelView(view);
  if (s.sheetDetent === 'small') s.setSheetDetent('medium');
}

export function closeDetail(): void {
  useAppStore.getState().setPanelView({ kind: 'list' });
}
